/**
 * "New Drop Coming Soon" teaser banner.
 * Left side: bold copy + CTA. Right side: portrait video of the upcoming jerseys.
 * Full-width, dark background, red accent stripes to match Hero styling.
 */
import { useEffect, useRef } from "react";

export default function DropTeaser() {
    const videoRef = useRef(null);
    const sectionRef = useRef(null);

    // Pause the video when off-screen to save bandwidth + CPU.
    useEffect(() => {
        if (!sectionRef.current || !videoRef.current) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const v = videoRef.current;
                    if (!v) return;
                    if (entry.isIntersecting) {
                        v.play().catch(() => {});
                    } else {
                        v.pause();
                    }
                });
            },
            { threshold: 0.2 },
        );
        io.observe(sectionRef.current);
        return () => io.disconnect();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="drop-teaser"
            data-testid="drop-teaser"
            className="relative w-full bg-black overflow-hidden border-y border-white/5"
        >
            {/* Ambient color glow */}
            <div className="absolute inset-0 pointer-events-none opacity-70 bg-[radial-gradient(circle_at_15%_50%,rgba(230,57,70,0.15),transparent_55%),radial-gradient(circle_at_85%_50%,rgba(29,78,216,0.18),transparent_55%)]" />

            <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center">
                {/* LEFT — copy + CTA */}
                <div className="order-2 md:order-1">
                    <div
                        className="inline-flex items-center gap-2 mb-6"
                        data-testid="drop-teaser-kicker"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                        </span>
                        <span className="text-red-500 text-xs tracking-[0.35em] font-display uppercase">
                            Incoming
                        </span>
                    </div>

                    <h2
                        data-testid="drop-teaser-title"
                        className="font-display text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight uppercase"
                    >
                        <span className="block">New Drop</span>
                        <span className="block text-red-500">Coming Soon.</span>
                    </h2>

                    <p
                        data-testid="drop-teaser-body"
                        className="mt-6 max-w-md text-zinc-300 text-base md:text-lg leading-relaxed"
                    >
                        Team-fit performance jerseys. Black, red, blue — every stitch
                        built for athletes who don&apos;t quit. Waitlist opens first, sizes
                        move fast.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <a
                            href="/drops/ascent"
                            data-testid="drop-teaser-cta"
                            className="group inline-flex items-center gap-3 bg-white text-black px-7 py-3.5 font-display uppercase tracking-[0.2em] text-sm hover:bg-red-500 hover:text-white transition-colors"
                        >
                            <span>Join the Waitlist</span>
                            <span className="transition-transform group-hover:translate-x-1">
                                →
                            </span>
                        </a>
                        <a
                            href="/shop"
                            data-testid="drop-teaser-shop"
                            className="text-white/70 hover:text-white text-xs tracking-[0.35em] font-display uppercase border-b border-white/20 hover:border-white pb-1 transition-colors"
                        >
                            Shop Current Drop
                        </a>
                    </div>

                    {/* Countdown / meta strip */}
                    <div className="mt-10 flex items-center gap-6 text-xs tracking-[0.3em] font-display uppercase text-white/40">
                        <div className="flex items-center gap-2">
                            <span className="h-px w-8 bg-white/30" />
                            <span>Jerseys</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="h-px w-8 bg-white/30" />
                            <span>Limited Run</span>
                        </div>
                    </div>
                </div>

                {/* RIGHT — portrait video */}
                <div className="order-1 md:order-2 relative">
                    <div className="relative aspect-[9/16] w-full max-w-[340px] md:max-w-[420px] mx-auto rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10">
                        <video
                            ref={videoRef}
                            data-testid="drop-teaser-video"
                            muted
                            loop
                            playsInline
                            autoPlay
                            preload="metadata"
                            poster="/brand/drop-teaser-poster.jpg"
                            className="absolute inset-0 w-full h-full object-cover"
                        >
                            <source src="/brand/drop-teaser.webm" type="video/webm" />
                            <source src="/brand/drop-teaser.mp4" type="video/mp4" />
                        </video>
                        {/* Subtle overlay + corner marker */}
                        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/50 via-transparent to-black/20" />
                        <div className="absolute top-3 left-3 flex items-center gap-2 text-white text-[10px] tracking-[0.3em] font-display uppercase">
                            <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                            Teaser
                        </div>
                        <div className="absolute bottom-3 right-3 text-white/80 text-[10px] tracking-[0.3em] font-display uppercase">
                            FGM · S/S 26
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom red accent stripe */}
            <div className="relative h-1 w-full bg-gradient-to-r from-red-600 via-red-500 to-red-600 opacity-80" />
        </section>
    );
}
